import {
    Box, IconButton, InputAdornment, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TextField, Tooltip, Typography, Button, Tab, Tabs,
    Dialog, DialogTitle, DialogContent, Grid, CircularProgress,
    Autocomplete
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import LockClockIcon from '@mui/icons-material/LockClock'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import BoltIcon from '@mui/icons-material/Bolt'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    ApiApproveWithdrawal, ApiApproveAchWithdrawal,
    ApiDepositToUser, ApiGetUsers, ApiGetWithdrawalRequests,
    ApiRejectWithdrawal, ApiRejectAchWithdrawal
} from '../../apiService/Core/Apicall'
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import { useDateRange } from '../../hooks/useDateRange'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'
import { usePersistedState } from '../../hooks/userPersistedState'

interface AchHold {
    isActive: boolean
    amount: number
    holdUntil: string | null
    releasedAt: string | null
    holdDurationHours: number
}

interface Fees {
    platform: number
    provider: number
    total: number
}

interface Transaction {
    _id: string
    transactionId: string
    amount: number
    netAmount: number
    status: string
    paymentProvider: string
    description: string
    direction: 'INCOMING' | 'OUTGOING'
    type: string
    currency: string
    bankAccountId: string
    fees: Fees
    achHold: AchHold
    createdAt: string
    completedAt: string | null
    failedAt: string | null
    initiatedAt: string
    errorCode: string | null
    errorMessage: string | null
    failureReason: string | null
    isFullyReconciled: boolean
    userId: string | {
        _id: string,
        firstName: string,
        lastName: string,
        email: string
    }
}

interface Pagination {
    total: number
    page: number
    limit: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
}

type PaymentProvider = 'AEROPAY' | 'WIRE'


const formatAmount = (amount: number) =>
    amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatDate = (date: string) =>
    new Date(date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

const formatTime = (date: string) =>
    new Date(date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

const getUserInfo = (userId: Transaction['userId']) => {
    if (typeof userId === 'object' && userId !== null) {
        return { name: `${userId.firstName} ${userId.lastName}`, email: userId.email }
    }
    return { name: '—', email: '—' }
}

const statusMap: Record<string, { color: string; bg: string; border: string }> = {
    COMPLETED: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
    PENDING: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f650' },
    PROCESSING: { color: '#f59e0b', bg: '#f59e0b18', border: '#f59e0b50' },
    FAILED: { color: '#ef4444', bg: '#ef444418', border: '#ef444450' },
    APPROVED: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
    CANCELLED: { color: '#9ca3af', bg: '#9ca3af18', border: '#9ca3af50' },
}

const StatusPill: React.FC<{ status: string; tooltip?: string }> = ({ status, tooltip }) => {
    const s = statusMap[status] || { color: '#9a9a9a', bg: '#9a9a9a18', border: '#9a9a9a50' }

    const pill = (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.5, py: 0.4, borderRadius: '6px', fontSize: '11px', fontWeight: 700,
            color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}`,
            letterSpacing: '0.03em', whiteSpace: 'nowrap',
            cursor: tooltip ? 'help' : 'default',
            borderBottom: tooltip ? `1px dashed ${s.color}60` : `1px solid ${s.border}`,
        }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            {status}
            {tooltip && (
                <Box sx={{ fontSize: 9, opacity: 0.7, ml: 0.3 }}>ⓘ</Box>
            )}
        </Box>
    )

    if (tooltip) {
        return (
            <Tooltip
                title={
                    <Box>
                        <Typography fontSize={10} color="#aaa" mb={0.3} letterSpacing="0.06em" textTransform="uppercase">
                            Reason
                        </Typography>
                        <Typography fontSize={12}>{tooltip}</Typography>
                    </Box>
                }
                arrow
                placement="top"
            >
                {pill}
            </Tooltip>
        )
    }

    return pill
}


const StatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333',
        borderRadius: '14px',
        p: 2.5,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.25s',
        '&:hover': { borderColor: color + '60' },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">{label}</Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
        {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)


const Transfers: React.FC = () => {
    const location = useLocation()
    const userId = location.state?.userId
    const {
        range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
    } = useDateRange('transfers', 'all')
    const [tab, setTab] = usePersistedState('transfers:tab', 0)
    const [providerTab, setProviderTab] = usePersistedState('transfers:providerTab', 0)
    const provider: PaymentProvider = providerTab === 0 ? 'AEROPAY' : 'WIRE'

    const [depositOpen, setDepositOpen] = useState(false)
    const [depositAmount, setDepositAmount] = useState('')
    const [depositError, setDepositError] = useState('')
    const [depositLoading, setDepositLoading] = useState(false)

    const [transactions, setTransactions] = useState<Transaction[]>([])
    const [pagination, setPagination] = useState<Pagination>({ total: 0, page: 1, limit: 10, totalPages: 1, hasNextPage: false, hasPrevPage: false })
    const [search, setSearch] = usePersistedState('transfers:search', '')
    const [page, setPage] = usePersistedState('transfers:page', 0)
    const [rowsPerPage, setRowsPerPage] = usePersistedState('transfers:rowsPerPage', 10)
    const [approvingId, setApprovingId] = useState<string | null>(null)
    const [rejectingId, setRejectingId] = useState<string | null>(null)


    const [depositStats, setDepositStats] = useState({ total: 0, count: 0, achHeldTotal: 0 })
    const [withdrawStats, setWithdrawStats] = useState({ total: 0, pendingCount: 0, pendingTotal: 0 })

    const [revealedRows, setRevealedRows] = useState<Record<string, {
        accountNumber: string
        routingNumber: string
        swiftCode: string,
        bsb: string
    }>>({})
    const [userSearch, setUserSearch] = useState('')
    const [userOptions, setUserOptions] = useState<any[]>([])
    const [selectedUser, setSelectedUser] = useState<any | null>(null)
    const STATUS_OPTIONS = ['PENDING', 'COMPLETED', 'CANCELLED', 'FAILED', 'PROCESSING']
    const [status, setStatus] = usePersistedState('transfers:status', '')
    const [debouncedSearch, setDebouncedSearch] = useState(search)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({
        open: false,
        message: '',
    })
    const [successPopup, setSuccessPopup] = useState<{ open: boolean; message: string }>({
        open: false,
        message: '',
    })
    const [rejectReasonOpen, setRejectReasonOpen] = useState(false)
    const [rejectReason, setRejectReason] = useState('')
    const [rejectReasonError, setRejectReasonError] = useState('')
    const [pendingRejectId, setPendingRejectId] = useState<string | null>(null)
    const [pendingRejectProvider, setPendingRejectProvider] = useState<string>('')
    const [confirmRejectOpen, setConfirmRejectOpen] = useState(false)
    const [confirmApproveOpen, setConfirmApproveOpen] = useState(false)
    const [pendingApproveTx, setPendingApproveTx] = useState<Transaction | null>(null)
    const navigate = useNavigate()

    const direction = tab === 0 ? 'INCOMING' : 'OUTGOING'

    const fetchTransactions = useCallback(async () => {
        try {
            const dateQuery = buildQuery()
            if (dateQuery === null) return

            const params = new URLSearchParams()
            if (userId) params.append('userId', userId)
            params.append('direction', direction)
            params.append('type', 'deposit,withdrawal')
            params.append('paymentProvider', provider)
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))
            if (debouncedSearch) params.append('search', debouncedSearch)
            if (status) params.append('status', status)
            if (dateQuery) {
                new URLSearchParams(dateQuery).forEach((value, key) => params.append(key, value))
            }
            const res = await ApiGetWithdrawalRequests(`?${params.toString()}`)
            if (res?.status) {
                const txns: Transaction[] = res.data?.transactions || []
                setTransactions(txns)
                setPagination(res.data?.pagination || {})

                if (tab === 0) {
                    setDepositStats({
                        total: res.data?.stats?.totalAmount ?? 0,
                        count: res.data?.pagination?.total ?? txns.length,
                        achHeldTotal: res.data?.stats?.achHeldTotal ?? 0,
                    })
                } else {
                    setWithdrawStats({
                        total: res.data?.pagination?.total ?? txns.length,
                        pendingCount: res.data?.stats?.pendingCount ?? 0,
                        pendingTotal: res.data?.stats?.pendingTotal ?? 0,
                    })
                }
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to fetch transactions',
            })
        }
    }, [userId, direction, provider, page, rowsPerPage, debouncedSearch, tab, status, buildQuery])

    useEffect(() => {
        fetchTransactions()
    }, [fetchTransactions])

    const prevTabRef = React.useRef({ tab, providerTab })
    useEffect(() => {
        const prev = prevTabRef.current
        if (prev.tab !== tab || prev.providerTab !== providerTab) {
            setPage(0)
            setSearch('')
            setStatus('')
        }
        prevTabRef.current = { tab, providerTab }
    }, [tab, providerTab, setPage, setSearch, setStatus])

    const handleDeposit = async () => {
        if (!depositAmount || isNaN(Number(depositAmount)) || Number(depositAmount) <= 0) {
            setDepositError('Enter a valid amount')
            return
        }
        const targetUserId = selectedUser?._id ?? userId
        if (!targetUserId) {
            toast.info('Please select a user')
            return
        }
        setDepositError('')
        setDepositLoading(true)
        try {
            const payload = { userId: targetUserId, amount: Number(depositAmount), currency: 'USD' }
            const res = await ApiDepositToUser(payload)
            if (res?.status) {
                setSuccessPopup({
                    open: true,
                    message: res?.message || 'Deposit added successfully',
                })
                setDepositOpen(false)
                setDepositAmount('')
                setSelectedUser(null)
                setTab(0)
                setPage(0)
                fetchTransactions()
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to add deposit',
            })

        } finally {
            setDepositLoading(false)
        }
    }

    useEffect(() => {
        if (!depositOpen) {
            setUserSearch('')
            setUserOptions([])
            setSelectedUser(null)
            return
        }

        const timer = setTimeout(async () => {
            try {
                const params = `?page=0&limit=10&isDeleted=false&search=${encodeURIComponent(userSearch)}`
                const res = await ApiGetUsers(params)
                if (res?.status) {
                    setUserOptions(res.data || [])
                }
            } catch (e: any) {
                setErrorPopup({
                    open: true,
                    message: e?.message || e?.response?.data?.message || e || 'Failed to fetch users',
                })
            }
        }, 500)

        return () => clearTimeout(timer)
    }, [userSearch, depositOpen])

    const handleApprove = async (tx: Transaction) => {
        const id = tx.transactionId
        setApprovingId(id)
        try {
            const data = { transactionId: id }
            const res = tx.paymentProvider === 'AEROPAY'
                ? await ApiApproveAchWithdrawal(data)
                : await ApiApproveWithdrawal(data)
            if (res?.status) {
                setSuccessPopup({
                    open: true,
                    message: res?.message || 'Withdrawal approved successfully',
                })
                fetchTransactions()
                await new Promise(r => setTimeout(r, 800))
                setTransactions(prev => prev.map(t => t.transactionId === id
                    ? { ...t, status: t.paymentProvider === 'AEROPAY' ? 'PROCESSING' : 'COMPLETED' }
                    : t))
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to approve',
            })
        } finally {
            setApprovingId(null)
        }
    }

    const handleReject = async (id: string, reason: string, paymentProvider: string) => {
        setRejectingId(id)
        try {
            const data = { transactionId: id, rejectReason: reason }
            const res = paymentProvider === 'AEROPAY'
                ? await ApiRejectAchWithdrawal(data)
                : await ApiRejectWithdrawal(data)
            if (res?.status) {
                setSuccessPopup({
                    open: true,
                    message: res?.message || 'Withdrawal rejected successfully',
                })
                fetchTransactions()
                await new Promise(r => setTimeout(r, 800))
                setTransactions(prev => prev.map(t => t.transactionId === id
                    ? { ...t, status: paymentProvider === 'AEROPAY' ? 'CANCELLED' : 'REJECTED' }
                    : t))
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to reject',
            })
        } finally {
            setRejectingId(null)
        }
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search)
        }, 500)

        return () => clearTimeout(timer)
    }, [search])

    const prevFiltersRef = React.useRef({ debouncedSearch, status, range, customStart, customEnd })
    useEffect(() => {
        const prev = prevFiltersRef.current
        const changed =
            prev.debouncedSearch !== debouncedSearch ||
            prev.status !== status ||
            prev.range !== range ||
            (prev.customStart?.valueOf() !== customStart?.valueOf()) ||
            (prev.customEnd?.valueOf() !== customEnd?.valueOf())
        if (changed) setPage(0)
        prevFiltersRef.current = { debouncedSearch, status, range, customStart, customEnd }
    }, [debouncedSearch, status, range, customStart, customEnd, setPage])

    const pendingCount = withdrawStats.pendingCount


    return (
        <div className='common-layout flex-column'>
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <Box>
                        <Typography className='fs-24 text-white fw-medium' sx={{ lineHeight: 1.1 }}>Transfers</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {pendingCount > 0 && (
                        <Box sx={{
                            background: '#ffffff60', border: '1px solid red',
                            borderRadius: '8px', px: 2, py: 0.8,
                            display: 'flex', alignItems: 'center', gap: 1
                        }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: 'red', animation: 'pulse 1.5s infinite' }} />
                            <Typography sx={{ color: 'red', fontSize: 12, fontWeight: 700 }}>
                                {pendingCount} Pending Withdrawal{pendingCount > 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        startIcon={<SyncAltIcon />}
                        onClick={() => navigate('/admin/auto-transactions')}
                        sx={{
                            borderRadius: '10px', fontWeight: 700, px: 3, py: 1,
                            background: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
                            '&:hover': { background: 'linear-gradient(135deg, #9061f9, #6d28d9)' },
                        }}
                    >
                        Auto Transactions
                    </Button>
                    <Button
                        variant="contained"
                        className='bg-orange'
                        startIcon={<AddIcon />}
                        onClick={() => setDepositOpen(true)}
                        sx={{ borderRadius: '10px', fontWeight: 700, px: 3, py: 1 }}
                    >
                        Add Deposit
                    </Button>
                </Box>
            </Box>

            <DateRangeFilter
                range={range}
                onRangeChange={handleRangeChange}
                customStart={customStart}
                onCustomStartChange={setCustomStart}
                customEnd={customEnd}
                onCustomEndChange={setCustomEnd}
            />

            <Grid container spacing={2} mb={2} mt={2}>
                {tab === 0 ? (
                    <>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <StatCard label="Total Deposits" value={`$${formatAmount(depositStats.total)}`}
                                sub={`${depositStats.count} total transactions`} color="#10b981"
                                icon={<ArrowDownwardIcon fontSize="small" />} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <StatCard label="ACH Held Amount" value={`$${formatAmount(depositStats.achHeldTotal)}`}
                                sub="Funds currently on hold" color="#f59e0b"
                                icon={<LockClockIcon fontSize="small" />} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <StatCard label="Transactions (Page)" value={String(transactions.length)}
                                sub={`of ${pagination.total} total`} color="#a78bfa"
                                icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />} />
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <StatCard label="Pending Withdrawals" value={`$${formatAmount(withdrawStats.pendingTotal)}`}
                                sub={`${withdrawStats.pendingCount} awaiting approval`} color="#ef4444"
                                icon={<ArrowUpwardIcon fontSize="small" />} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <StatCard label="Total Requests" value={String(pagination.total)}
                                sub="All outgoing requests" color="#f2674a"
                                icon={<ArrowUpwardIcon fontSize="small" />} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <StatCard label="Transactions (Page)" value={String(transactions.length)}
                                sub={`of ${pagination.total} total`} color="#a78bfa"
                                icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />} />
                        </Grid>
                    </>
                )}
            </Grid>

            <Box className="bg-dark-color border-10 p-0 mt-2" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: '16px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            sx={{
                                minHeight: 40,
                                background: '#1e1e1e',
                                borderRadius: '10px',
                                p: 0.5,
                                border: '1px solid #333',
                                '& .MuiTabs-indicator': { display: 'none' },
                                '& .MuiTab-root': {
                                    minHeight: 36, borderRadius: '8px', fontSize: 13, fontWeight: 600,
                                    color: '#6f6f6f', textTransform: 'none', px: 3, py: 0.5,
                                    transition: 'all 0.2s',
                                },
                                '& .Mui-selected': {
                                    color: '#fff !important',
                                    background: 'linear-gradient(135deg, #f2674a, #d94f35)',
                                    boxShadow: '0 2px 8px rgba(242,103,74,0.4)',
                                },
                            }}
                        >
                            <Tab label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ArrowDownwardIcon sx={{ fontSize: 15 }} />
                                    Deposits
                                    {tab === 0 && pagination.total > 0 && (
                                        <Box sx={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', px: 0.8, fontSize: 10, lineHeight: '18px' }}>
                                            {pagination.total}
                                        </Box>
                                    )}
                                </Box>
                            } />
                            <Tab label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ArrowUpwardIcon sx={{ fontSize: 15 }} />
                                    Withdrawals
                                    {pendingCount > 0 && (
                                        <Box sx={{ background: 'rgba(239,68,68,0.3)', borderRadius: '10px', px: 0.8, fontSize: 10, lineHeight: '18px', color: '#ef4444' }}>
                                            {pendingCount}
                                        </Box>
                                    )}
                                </Box>
                            } />
                        </Tabs>

                        <Tabs
                            value={providerTab}
                            onChange={(_, v) => setProviderTab(v)}
                            sx={{
                                minHeight: 40,
                                background: '#1e1e1e',
                                borderRadius: '10px',
                                p: 0.5,
                                border: '1px solid #333',
                                '& .MuiTabs-indicator': { display: 'none' },
                                '& .MuiTab-root': {
                                    minHeight: 36, borderRadius: '8px', fontSize: 13, fontWeight: 600,
                                    color: '#6f6f6f', textTransform: 'none', px: 2.5, py: 0.5,
                                    transition: 'all 0.2s',
                                },
                                '& .Mui-selected': {
                                    color: '#fff !important',
                                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                    boxShadow: '0 2px 8px rgba(59,130,246,0.4)',
                                },
                            }}
                        >
                            <Tab label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BoltIcon sx={{ fontSize: 15 }} />
                                    Aeropay
                                </Box>
                            } />
                            <Tab label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountBalanceIcon sx={{ fontSize: 15 }} />
                                    Wire
                                </Box>
                            } />
                        </Tabs>
                    </Box>

                    <Box className="d-flex align-items-center gap-2" sx={{ flexWrap: 'wrap' }}>
                        <Autocomplete
                            size="small"
                            value={status}
                            disableClearable={!status}
                            onChange={(_, newValue) => { setStatus(newValue || ''); }}
                            options={STATUS_OPTIONS}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Status" sx={searchFieldStyle}
                                    InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                />
                            )}
                            PaperComponent={({ children }) => (
                                <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                    {children}
                                </Box>
                            )}
                        />

                        <TextField
                            placeholder="Search by User, Email"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); }}
                            size="small"
                            sx={searchFieldStyle}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon className='color-primary' />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Box>

                {tab === 0 && (
                    <TableContainer sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                            <TableHead>
                                <TableRow>
                                    {['#', 'User', 'Transaction ID', 'Date & Time', 'Amount', 'Net Amount', 'Provider', 'ACH Hold', 'Fees', 'Status'].map(h => (
                                        <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.length > 0 ? transactions.map((tx, i) => (
                                    <TableRow
                                        key={tx._id}
                                        sx={{ ...trHover }}
                                    >
                                        <TableCell sx={tdStyle}>{page * rowsPerPage + i + 1}</TableCell>
                                        <TableCell
                                            sx={{ ...tdStyle, cursor: 'pointer' }}
                                            onClick={() => {
                                                const u = tx.userId as { _id: string; firstName: string; lastName: string; email: string }
                                                navigate('/admin/user-activity', {
                                                    state: {
                                                        userId: u._id,
                                                        userName: `${u.firstName} ${u.lastName}`
                                                    }
                                                })
                                            }}
                                        >
                                            <Typography fontSize={13} fontWeight={600} color="#e0e0e0">
                                                {getUserInfo(tx.userId).name}
                                            </Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{getUserInfo(tx.userId).email}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Tooltip title={tx.transactionId} arrow>
                                                <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {tx.transactionId}
                                                </Typography>
                                            </Tooltip>
                                            <Typography fontSize={10} color="#4a4a4a" sx={{ fontFamily: 'monospace' }}>
                                                {tx.bankAccountId}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} color="#e0e0e0">{formatDate(tx.createdAt)}</Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{formatTime(tx.createdAt)}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={14} fontWeight={700} color="#10b981">
                                                +{tx.currency === 'AUD' ? 'A$' : '$'}{formatAmount(tx.amount)}
                                            </Typography>
                                            <Box sx={{ display: 'inline-block', px: 1, py: 0.2, borderRadius: '4px', fontSize: 9, fontWeight: 700, color: '#f2674a', background: '#f2674a12', border: '1px solid #f2674a25', mt: 0.3 }}>
                                                {tx.currency}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} color="#9a9a9a">{tx.currency === 'AUD' ? 'A$' : '$'}{formatAmount(tx.netAmount)}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Box sx={{ display: 'inline-block', px: 1.2, py: 0.3, borderRadius: '5px', fontSize: 10, fontWeight: 700, color: '#3b82f6', background: '#3b82f615', border: '1px solid #3b82f630' }}>
                                                {tx.paymentProvider}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            {tx.achHold?.isActive ? (
                                                <Box>
                                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.3, borderRadius: '5px', fontSize: 10, fontWeight: 700, color: '#f59e0b', background: '#f59e0b15', border: '1px solid #f59e0b40' }}>
                                                        <LockClockIcon sx={{ fontSize: 10 }} />
                                                        {tx.currency === 'AUD' ? 'A$' : '$'}{formatAmount(tx.achHold.amount)}
                                                    </Box>
                                                    {tx.achHold.holdUntil && (
                                                        <Typography fontSize={9} color="#5a5a5a" mt={0.3}>
                                                            until {formatDate(tx.achHold.holdUntil)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Typography fontSize={11} color="#3a3a3a" fontStyle="italic">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            {tx.fees?.total > 0 ? (
                                                <Tooltip title={`Platform: $${formatAmount(tx.fees.platform)} | Provider: $${formatAmount(tx.fees.provider)}`} arrow>
                                                    <Typography fontSize={12} color="#9a9a9a">${formatAmount(tx.fees.total)}</Typography>
                                                </Tooltip>
                                            ) : (
                                                <Typography fontSize={11} color="#3a3a3a">-</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={tdStyle}><StatusPill status={tx.status} /></TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                            <ArrowDownwardIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                            <Typography color="#4a4a4a" fontSize={13}>No deposit transactions found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {tab === 1 && (
                    <TableContainer sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                            <TableHead>
                                <TableRow>
                                    {['#', 'User', 'Transaction ID', 'Date & Time', 'Amount', 'Net Amount', 'Provider', 'Bank Details', 'Reveal', 'Fees', 'Description', 'Status', 'Action'].map(h => (
                                        <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.length > 0 ? transactions.map((tx, i) => {
                                    return (
                                        <TableRow
                                            key={tx._id}
                                            sx={{ ...trHover }}
                                        >
                                            <TableCell sx={tdStyle}>{page * rowsPerPage + i + 1}</TableCell>
                                            <TableCell
                                                sx={{ ...tdStyle, cursor: 'pointer' }}
                                                onClick={() => {
                                                    const u = tx.userId as { _id: string; firstName: string; lastName: string; email: string }
                                                    navigate('/admin/user-activity', {
                                                        state: {
                                                            userId: u._id,
                                                            userName: `${u.firstName} ${u.lastName}`
                                                        }
                                                    })
                                                }}
                                            >
                                                <Typography fontSize={13} fontWeight={600} color="#e0e0e0">
                                                    {getUserInfo(tx.userId).name}
                                                </Typography>
                                                <Typography fontSize={10} color="#5a5a5a">{getUserInfo(tx.userId).email}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Tooltip title={tx.transactionId} arrow>
                                                    <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {tx.transactionId}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} color="#e0e0e0">{formatDate(tx.createdAt)}</Typography>
                                                <Typography fontSize={10} color="#5a5a5a">{formatTime(tx.createdAt)}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={14} fontWeight={700} color="#ef4444">
                                                    -${formatAmount(tx.amount)}
                                                </Typography>
                                                <Box sx={{ display: 'inline-block', px: 1, py: 0.2, borderRadius: '4px', fontSize: 9, fontWeight: 700, color: '#f2674a', background: '#f2674a12', border: '1px solid #f2674a25', mt: 0.3 }}>
                                                    {tx.currency}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Typography fontSize={13} color="#9a9a9a">${formatAmount(tx.netAmount)}</Typography>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <Box sx={{ display: 'inline-block', px: 1.2, py: 0.3, borderRadius: '5px', fontSize: 10, fontWeight: 700, color: '#3b82f6', background: '#3b82f615', border: '1px solid #3b82f630' }}>
                                                    {tx.paymentProvider}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                {(() => {
                                                    const revealed = revealedRows[tx.transactionId]
                                                    const bd = (tx as any).bankDetails
                                                    return (
                                                        <Box>
                                                            <Typography fontSize={12} color="#9a9a9a" fontWeight={600}>{bd?.bankName || '—'}</Typography>
                                                            <Typography fontSize={11} color="#7a7a7a">{bd?.accountHolderName || '—'}</Typography>
                                                            {revealed ? (
                                                                <Box sx={{ mt: 0.5, p: 1, borderRadius: '6px', background: '#10b98110', border: '1px solid #10b98130' }}>
                                                                    <Typography fontSize={10} color="#10b981" sx={{ fontFamily: 'monospace' }}>ACC: {revealed.accountNumber}</Typography>
                                                                    <Typography fontSize={10} color="#10b981" sx={{ fontFamily: 'monospace' }}>RTG: {revealed.routingNumber}</Typography>
                                                                    <Typography fontSize={10} color="#10b981" sx={{ fontFamily: 'monospace' }}>SWT: {revealed.swiftCode}</Typography>
                                                                    <Typography fontSize={10} color="#10b981" sx={{ fontFamily: 'monospace' }}>BSB: {revealed.bsb}</Typography>
                                                                </Box>
                                                            ) : (
                                                                <Typography fontSize={10} color="#5a5a5a" sx={{ fontFamily: 'monospace' }}>{bd?.maskedAccountNumber || '****'}</Typography>
                                                            )}
                                                        </Box>
                                                    )
                                                })()}
                                            </TableCell>

                                            <TableCell sx={tdStyle}>
                                                {(() => {
                                                    const revealed = revealedRows[tx.transactionId]
                                                    const bd = (tx as any).bankDetails
                                                    if (!bd) return <Typography fontSize={11} color="#3a3a3a">—</Typography>
                                                    return revealed ? (
                                                        <Button
                                                            startIcon={<LockIcon sx={{ color: "#10b981" }} />}
                                                            size="small"
                                                            onClick={() => setRevealedRows(prev => {
                                                                const next = { ...prev }
                                                                delete next[tx.transactionId]
                                                                return next
                                                            })}
                                                            sx={{
                                                                fontSize: 11, fontWeight: 700, borderRadius: '7px',
                                                                color: '#10b981', border: '1px solid #10b98140',
                                                                background: '#10b98110', px: 1.5, py: 0.5, minWidth: 70,
                                                                '&:hover': { background: '#10b98120' }
                                                            }}
                                                        >
                                                            Hide
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            startIcon={<LockOpenIcon sx={{ color: "#f59e0b" }} />}
                                                            onClick={() => {
                                                                const bd = (tx as any).bankDetails
                                                                setRevealedRows(prev => ({
                                                                    ...prev,
                                                                    [tx.transactionId]: {
                                                                        accountNumber: bd?.accountNumber,
                                                                        routingNumber: bd?.routingNumber,
                                                                        swiftCode: bd?.swiftCode,
                                                                        bsb: bd?.bsbNumber
                                                                    }
                                                                }))
                                                            }}
                                                            sx={{
                                                                fontSize: 11, fontWeight: 700, borderRadius: '7px',
                                                                color: '#f59e0b', border: '1px solid #f59e0b40',
                                                                background: '#f59e0b10', px: 1.5, py: 0.5, minWidth: 70,
                                                                '&:hover': { background: '#f59e0b20' }
                                                            }}
                                                        >
                                                            Reveal
                                                        </Button>
                                                    )
                                                })()}
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                {tx.fees?.total > 0 ? (
                                                    <Tooltip title={`Platform: $${formatAmount(tx.fees.platform)} | Provider: $${formatAmount(tx.fees.provider)}`} arrow>
                                                        <Typography fontSize={12} color="#9a9a9a">${formatAmount(tx.fees.total)}</Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography fontSize={11} color="#3a3a3a">$0.00</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ ...tdStyle, color: '#7a7a7a', fontSize: 12, maxWidth: 180 }}>
                                                <Tooltip title={tx.description} arrow>
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 160 }}>
                                                        {tx.description || '—'}
                                                    </span>
                                                </Tooltip>
                                                {tx.errorMessage && (
                                                    <Tooltip title={tx.errorMessage} arrow>
                                                        <Typography fontSize={9} color="#ef4444" sx={{ cursor: 'help', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                                                            ⚠ {tx.errorMessage}
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                <StatusPill
                                                    status={tx.status}
                                                    tooltip={tx.failureReason || tx.errorMessage || undefined}
                                                />
                                            </TableCell>
                                            <TableCell sx={tdStyle}>
                                                {tx.status === 'PENDING' ? (
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            disabled={approvingId === tx.transactionId}
                                                            onClick={() => {
                                                                setPendingApproveTx(tx)
                                                                setConfirmApproveOpen(true)
                                                            }}
                                                            sx={{
                                                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                                                color: '#fff', fontWeight: 700, fontSize: 11,
                                                                borderRadius: '7px', px: 2, py: 0.6,
                                                                boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
                                                                '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
                                                                '&:disabled': { opacity: 0.5 },
                                                                minWidth: 90,
                                                            }}
                                                        >
                                                            {approvingId === tx.transactionId
                                                                ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                                                                : '✓ Approve'
                                                            }
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            disabled={approvingId === tx.transactionId || rejectingId === tx.transactionId}
                                                            onClick={() => {
                                                                setPendingRejectId(tx.transactionId)
                                                                setPendingRejectProvider(tx.paymentProvider)
                                                                setRejectReason('')
                                                                setRejectReasonError('')
                                                                setRejectReasonOpen(true)
                                                            }}
                                                            sx={{
                                                                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                                                                color: '#fff', fontWeight: 700, fontSize: 11,
                                                                borderRadius: '7px', px: 2, py: 0.6,
                                                                boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                                                                '&:hover': { background: 'linear-gradient(135deg, #dc2626, #b91c1c)' },
                                                                '&:disabled': { opacity: 0.5 },
                                                                minWidth: 90,
                                                            }}
                                                        >
                                                            ✕ Reject
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <Typography fontSize={11} color="#3a3a3a" fontStyle="italic">—</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                }) : (
                                    <TableRow>
                                        <TableCell colSpan={13} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                            <ArrowUpwardIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                            <Typography color="#4a4a4a" fontSize={13}>No withdrawal transactions found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton showLastButton
                    sx={paginationStyle}
                />
            </Box>

            <Dialog
                open={depositOpen}
                onClose={() => { setDepositOpen(false); setDepositAmount(''); setDepositError('') }}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#242424',
                        borderRadius: '16px',
                        boxShadow: '0 0 60px rgba(242,103,74,.25)',
                        border: '1px solid #333',
                        overflow: 'hidden',
                    }
                }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #f2674a, #d94f35)',
                    color: '#fff', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', fontWeight: 700, mb: 0, fontSize: 15,
                    boxShadow: '0 4px 20px rgba(242,103,74,.4)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ArrowDownwardIcon fontSize="small" />
                        </Box>
                        Add Deposit
                    </Box>
                    <IconButton onClick={() => setDepositOpen(false)} size="small">
                        <CloseIcon sx={{ color: '#fff', fontSize: 18 }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ backgroundColor: '#242424', pt: '20px !important' }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography fontSize={11} color="#5a5a5a" mb={1} letterSpacing="0.08em" textTransform="uppercase">
                            Select User
                        </Typography>
                        <Autocomplete
                            options={userOptions}
                            getOptionLabel={(o) => `${o.email}`}
                            filterOptions={(x) => x}
                            value={selectedUser}
                            onChange={(_, val) => setSelectedUser(val)}
                            inputValue={userSearch}
                            onInputChange={(_, val) => setUserSearch(val)}
                            isOptionEqualToValue={(a, b) => a._id === b._id}
                            renderOption={(props, o) => (
                                <Box component="li" {...props} key={o._id}
                                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: '8px !important' }}>
                                    <Typography fontSize={13} fontWeight={600} color="#e0e0e0">
                                        {o.firstName} {o.lastName}
                                    </Typography>
                                    <Typography fontSize={11} color="#6a6a6a">{o.email}</Typography>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by name or email…"
                                    sx={{
                                        mb: 2.5,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px', color: '#fff',
                                            '& fieldset': { borderColor: '#333' },
                                            '&:hover fieldset': { borderColor: '#f2674a50' },
                                            '&.Mui-focused fieldset': { borderColor: '#f2674a' },
                                        },
                                        '& input': { color: '#fff', fontSize: 13 },
                                        '& input::placeholder': { color: '#555', opacity: 1 },
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            PaperComponent={({ children }) => (
                                <Box sx={{
                                    background: '#1e1e1e',
                                    border: '1px solid #333',
                                    borderRadius: '10px',
                                    mt: 0.5,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                    '& .MuiAutocomplete-listbox': { p: 0.5 },
                                    '& .MuiAutocomplete-option': {
                                        borderRadius: '7px', mb: 0.3,
                                        '&:hover, &.Mui-focused': { background: '#f2674a12 !important' },
                                    },
                                }}>
                                    {children}
                                </Box>
                            )}
                            noOptionsText={
                                <Typography fontSize={12} color="#5a5a5a" sx={{ p: 1 }}>
                                    {userSearch ? 'No users found' : 'Type to search users…'}
                                </Typography>
                            }
                        />
                        <Typography fontSize={11} color="#5a5a5a" mb={1.5} letterSpacing="0.08em" textTransform="uppercase">
                            Enter deposit amount in USD
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="0.00"
                            type="number"
                            value={depositAmount}
                            onChange={(e) => setDepositAmount(e.target.value)}
                            error={!!depositError}
                            helperText={depositError}
                            sx={inputStyle}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography sx={{ color: '#f2674a', fontWeight: 800, fontSize: 20, lineHeight: 1 }}>$</Typography>
                                    </InputAdornment>
                                ),
                                sx: { fontSize: 22, fontWeight: 700, color: '#fff' }
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                        {[100, 250, 500, 1000, 5000].map((amt) => (
                            <Box
                                key={amt}
                                onClick={() => setDepositAmount(String(amt))}
                                sx={{
                                    px: 1.5, py: 0.6, borderRadius: '7px', cursor: 'pointer',
                                    fontSize: 12, fontWeight: 600, border: '1px solid #333',
                                    color: depositAmount === String(amt) ? '#f2674a' : '#6f6f6f',
                                    background: depositAmount === String(amt) ? '#f2674a15' : '#1e1e1e',
                                    borderColor: depositAmount === String(amt) ? '#f2674a50' : '#333',
                                    transition: 'all 0.15s',
                                    '&:hover': { borderColor: '#f2674a50', color: '#f2674a' }
                                }}
                            >
                                ${amt.toLocaleString()}
                            </Box>
                        ))}
                    </Box>
                </DialogContent>

                <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 1.5 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => { setDepositOpen(false); setDepositAmount(''); setDepositError('') }}
                        sx={{ borderColor: '#333', color: '#7a7a7a', borderRadius: '10px', '&:hover': { borderColor: '#555', color: '#fff' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={depositLoading}
                        onClick={() => setConfirmOpen(true)}
                        sx={{
                            background: 'linear-gradient(135deg, #f2674a, #d94f35)',
                            borderRadius: '10px', fontWeight: 700,
                            boxShadow: '0 4px 16px rgba(242,103,74,.4)',
                            '&:hover': { background: 'linear-gradient(135deg, #e05a3f, #c44530)' },
                        }}
                    >
                        Confirm Deposit
                    </Button>
                </Box>
            </Dialog>
            <Dialog
                open={rejectReasonOpen}
                onClose={() => { setRejectReasonOpen(false); setRejectReason(''); setRejectReasonError('') }}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#242424',
                        borderRadius: '16px',
                        boxShadow: '0 0 60px rgba(239,68,68,.25)',
                        border: '1px solid #333',
                        overflow: 'hidden',
                    }
                }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: '#fff', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', fontWeight: 700, mb: 0, fontSize: 15,
                    boxShadow: '0 4px 20px rgba(239,68,68,.4)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: '8px',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <CloseIcon fontSize="small" />
                        </Box>
                        Reject Withdrawal
                    </Box>
                    <IconButton onClick={() => setRejectReasonOpen(false)} size="small">
                        <CloseIcon sx={{ color: '#fff', fontSize: 18 }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ backgroundColor: '#242424', pt: '20px !important' }}>
                    <Typography fontSize={11} color="#5a5a5a" mb={1.5} letterSpacing="0.08em" textTransform="uppercase">
                        Reason for rejection
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={6}
                        placeholder="Enter the reason for rejecting this withdrawal request…"
                        value={rejectReason}
                        onChange={(e) => { setRejectReason(e.target.value); setRejectReasonError('') }}
                        error={!!rejectReasonError}
                        helperText={rejectReasonError}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px', color: '#fff',
                                '& fieldset': { borderColor: '#333' },
                                '&:hover fieldset': { borderColor: '#ef444450' },
                                '&.Mui-focused fieldset': { borderColor: '#ef4444' },
                            },
                            '& textarea': { color: '#fff', fontSize: 13 },
                            '& textarea::placeholder': { color: '#555', opacity: 1 },
                            '& .MuiFormHelperText-root': { color: '#ef4444', fontSize: 11 },
                        }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                        {[
                            'Insufficient documentation',
                            'Suspicious activity',
                            'Exceeds daily limit',
                            'Account under review',
                            'Duplicate request',
                        ].map((reason) => (
                            <Box
                                key={reason}
                                onClick={() => { setRejectReason(reason); setRejectReasonError('') }}
                                sx={{
                                    px: 1.5, py: 0.6, borderRadius: '7px', cursor: 'pointer',
                                    fontSize: 11, fontWeight: 600, border: '1px solid #333',
                                    color: rejectReason === reason ? '#ef4444' : '#6f6f6f',
                                    background: rejectReason === reason ? '#ef444415' : '#1e1e1e',
                                    borderColor: rejectReason === reason ? '#ef444450' : '#333',
                                    transition: 'all 0.15s',
                                    '&:hover': { borderColor: '#ef444450', color: '#ef4444' }
                                }}
                            >
                                {reason}
                            </Box>
                        ))}
                    </Box>
                </DialogContent>

                <Box sx={{ px: 3, pb: 3, pt: 1, display: 'flex', gap: 1.5 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => { setRejectReasonOpen(false); setRejectReason(''); setRejectReasonError('') }}
                        sx={{ borderColor: '#333', color: '#7a7a7a', borderRadius: '10px', '&:hover': { borderColor: '#555', color: '#fff' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => {
                            if (!rejectReason.trim()) {
                                setRejectReasonError('Please provide a reason for rejection')
                                return
                            }
                            setRejectReasonOpen(false)
                            setConfirmRejectOpen(true)
                        }}
                        sx={{
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            borderRadius: '10px', fontWeight: 700,
                            boxShadow: '0 4px 16px rgba(239,68,68,.4)',
                            '&:hover': { background: 'linear-gradient(135deg, #dc2626, #b91c1c)' },
                        }}
                    >
                        Continue
                    </Button>
                </Box>
            </Dialog>
            <PopupDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => { setConfirmOpen(false); handleDeposit() }}
                loading={depositLoading}
                variant="deposit"
                title="Confirm Deposit"
                message={`You are about to deposit funds into ${selectedUser?.email ?? 'the selected'}'s account.`}
                confirmText="Confirm Deposit"
                details={[
                    {
                        label: 'Recipient',
                        value: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : '—',
                        sub: selectedUser?.email ?? '—',
                    },
                    {
                        label: 'Amount',
                        value: depositAmount ? `$${Number(depositAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00',
                    },
                    {
                        label: 'Currency',
                        value: 'USD',
                    },
                    {
                        label: 'Timestamp',
                        value: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
                        mono: true,
                    },
                ]}
                notice="This action cannot be undone. Please verify the recipient and amount carefully before confirming."
            />

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.3; }
                }
            `}</style>
            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
            <PopupDialog
                open={successPopup.open}
                onClose={() => setSuccessPopup({ open: false, message: '' })}
                onConfirm={() => setSuccessPopup({ open: false, message: '' })}
                variant="success"
                title="Success"
                message={successPopup.message}
                confirmText="Done"
                cancelText="Close"
            />
            <PopupDialog
                open={confirmApproveOpen}
                onClose={() => setConfirmApproveOpen(false)}
                onConfirm={() => {
                    setConfirmApproveOpen(false)
                    if (pendingApproveTx) handleApprove(pendingApproveTx)
                }}
                loading={!!approvingId}
                variant="deposit"
                title="Confirm Approval"
                message="You are about to approve this withdrawal request."
                confirmText="Confirm Approve"
                details={[
                    {
                        label: 'Transaction ID',
                        value: pendingApproveTx?.transactionId ?? '—',
                        mono: true,
                    },
                    {
                        label: 'Provider',
                        value: pendingApproveTx?.paymentProvider ?? '—',
                    },
                    {
                        label: 'Amount',
                        value: pendingApproveTx
                            ? `$${formatAmount(pendingApproveTx.amount)}`
                            : '—',
                    },
                    {
                        label: 'Timestamp',
                        value: new Date().toLocaleString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                        }),
                        mono: true,
                    },
                ]}
                notice={
                    pendingApproveTx?.paymentProvider === 'AEROPAY'
                        ? 'Approving will initiate an ACH payout via Aeropay. Status will move to PROCESSING.'
                        : 'Approving will mark this wire withdrawal as APPROVED.'
                }
            />
            <PopupDialog
                open={confirmRejectOpen}
                onClose={() => setConfirmRejectOpen(false)}
                onConfirm={() => {
                    setConfirmRejectOpen(false)
                    if (pendingRejectId) handleReject(pendingRejectId, rejectReason, pendingRejectProvider)
                }}
                loading={!!rejectingId}
                variant="error"
                title="Confirm Rejection"
                message="You are about to reject this request. This action cannot be undone."
                confirmText="Confirm Reject"
                details={[
                    {
                        label: 'Transaction ID',
                        value: pendingRejectId ?? '—',
                        mono: true,
                    },
                    {
                        label: 'Reason',
                        value: rejectReason,
                    },
                    {
                        label: 'Timestamp',
                        value: new Date().toLocaleString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                        }),
                        mono: true,
                    },
                ]}
                notice="The user will be notified of this rejection along with the reason provided."
            />
        </div>
    )
}

export default Transfers


const thStyle = {
    backgroundColor: '#241815',
    color: '#ff6a3d',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    py: 1.5,
    borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
}

const tdStyle = {
    color: '#c0c0c0',
    py: 1.4,
    fontSize: 13,
    whiteSpace: 'nowrap' as const,
}

const trHover = {
    transition: 'background 0.15s',
    '&:hover': { background: '#ff6a3d08' },
}

const searchFieldStyle = {
    width: 280,
    background: 'transparent',
    borderRadius: '7px',
    border: '1px solid #898989',
    transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: 13 },
    '& input::placeholder': { color: '#555', opacity: 1 },
}

const paginationStyle = {
    color: '#6a6a6a', borderTop: '1px solid #252525',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
    '& .MuiSelect-select': { color: '#6a6a6a' },
    '& .MuiSelect-icon': { color: '#6a6a6a' },
    '& .MuiIconButton-root': { color: '#6a6a6a' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
}

const inputStyle = {
    '& input': { color: '#fff', fontSize: 22, fontWeight: 700, py: 1.5 },
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        '& fieldset': { borderColor: '#333' },
        '&:hover fieldset': { borderColor: '#f2674a50' },
        '&.Mui-focused fieldset': { borderColor: '#f2674a' },
    },
    '& .MuiFormHelperText-root': { color: '#ef4444', fontSize: 11 },
}